/**
 * Apple Sign In Integration for Natively SDK
 * 
 * This module provides utilities for signing in with Apple using the NativelyAppleSignInService.
 * Apple Sign In is only available on iOS native devices.
 * 
 * IMPORTANT NOTES:
 * - Apple allows users to hide their email addresses when logging into an app
 * - If hidden, Apple generates a unique forwarding email address
 * - This forwarding email is only revealed during the user's FIRST login
 * - In subsequent logins, the email field will be empty (null/undefined)
 * - You MUST store the email on first login, as you won't get it again
 * - The 'subject' field is always available and is the unique user identifier
 */

import { supabase } from '@/integrations/supabase/client';
import { isAppleSignInAvailable, waitForAppleSignInSDK } from '@/lib/platform';

export interface AppleSignInResult {
  success: boolean;
  isFirstTime?: boolean;
  email?: string;
  subject?: string;
  givenName?: string;
  familyName?: string;
  error?: string;
}

/**
 * Check if Apple Sign In is available on this device
 */
export const checkAppleSignInAvailability = async (): Promise<boolean> => {
  // Wait for SDK to load (with 5 second timeout)
  const sdkLoaded = await waitForAppleSignInSDK(5000, 500);
  return sdkLoaded && isAppleSignInAvailable();
};

/**
 * Initiate Apple Sign In flow
 * Returns the Apple Sign In response data
 */
export const initiateAppleSignIn = async (): Promise<AppleSignInResult> => {
  console.log('[Apple Sign In] Starting Apple Sign In flow...');

  // Check if SDK is available
  if (!window.NativelyAppleSignInService) {
    const error = 'Apple Sign In SDK not available';
    console.error('[Apple Sign In]', error);
    return {
      success: false,
      error,
    };
  }

  try {
    // Create service instance
    const appleService = new window.NativelyAppleSignInService();
    console.log('[Apple Sign In] Service instance created');

    // Create a promise that resolves when the callback is called
    const result = await new Promise<AppleSignInResult>((resolve) => {
      appleService.signin((resp) => {
        console.log('[Apple Sign In] Response received:', {
          status: resp.status,
          hasEmail: !!resp.email,
          hasSubject: !!resp.subject,
          hasGivenName: !!resp.givenname,
          hasFamilyName: !!resp.familyname,
          isInitial: resp.initial,
        });

        if (resp.status) {
          // Success
          resolve({
            success: true,
            isFirstTime: resp.initial,
            email: resp.email,
            subject: resp.subject,
            givenName: resp.givenname,
            familyName: resp.familyname,
          });
        } else {
          // Error
          resolve({
            success: false,
            error: resp.message || 'Apple Sign In failed',
          });
        }
      });
    });

    return result;
  } catch (error: any) {
    console.error('[Apple Sign In] Error:', error);
    return {
      success: false,
      error: error.message || 'Failed to initiate Apple Sign In',
    };
  }
};

/**
 * Sign in to Supabase using Apple Sign In with hCaptcha protection
 * 
 * This uses the native Apple Sign In SDK to get credentials,
 * validates hCaptcha server-side, then creates/signs in the user.
 * 
 * Bot protection:
 * - hCaptcha validation (same as email/password sign-in)
 * - Apple's device-level authentication
 * - Server-side verification
 * 
 * This properly handles:
 * - First-time users (with email)
 * - Returning users (without email - Apple doesn't provide it again)
 * - Session creation for both scenarios
 * - Account linking when email matches existing user
 * 
 * @param captchaToken - hCaptcha token for bot protection
 * @param showToast - Optional toast notification function
 */
export const signInWithApple = async (
  captchaToken: string,
  showToast?: (message: string, type: 'success' | 'error' | 'info') => void
): Promise<{
  success: boolean;
  error?: string;
  data?: any;
}> => {
  if (!captchaToken) {
    return {
      success: false,
      error: 'Captcha token is required',
    };
  }

  // Check if SDK is available
  if (!window.NativelyAppleSignInService) {
    return {
      success: false,
      error: 'Apple Sign In SDK not available',
    };
  }

  try {
    // Create service instance
    const appleService = new window.NativelyAppleSignInService();

    // Get Apple credentials via native SDK
    const appleCredentials = await new Promise<any>((resolve, reject) => {
      appleService.signin((resp) => {
        console.log('[Apple Sign In] SDK Response:', {
          status: resp.status,
          hasEmail: !!resp.email,
          hasSubject: !!resp.subject,
          isInitial: resp.initial,
          message: resp.message,
        });
        
        if (resp.status) {
          resolve({
            email: resp.email,
            subject: resp.subject,
            givenName: resp.givenname,
            familyName: resp.familyname,
            isFirstTime: resp.initial,
          });
        } else {
          // Check if user cancelled/dismissed the dialog
          const errorMsg = resp.message || 'Apple Sign In failed';
          const isCancellation = errorMsg.toLowerCase().includes('cancel') || 
                                 errorMsg.toLowerCase().includes('dismiss') ||
                                 errorMsg.toLowerCase().includes('closed');
          
          const error = new Error(errorMsg);
          (error as any).isCancellation = isCancellation;
          reject(error);
        }
      });
    });

    const { data: sessionData } = await supabase.auth.getSession();
    
    const result = await supabase.functions.invoke('apple-sign-in', {
      body: {
        apple_id: appleCredentials.subject,
        captcha_token: captchaToken,
        email: appleCredentials.email,
        given_name: appleCredentials.givenName,
        family_name: appleCredentials.familyName,
        is_first_time: appleCredentials.isFirstTime,
      },
      headers: sessionData?.session ? {
        Authorization: `Bearer ${sessionData.session.access_token}`
      } : undefined,
    });

    if (result.error) {
      throw result.error;
    }

    const data = result.data;

    if (!data?.success) {
      throw new Error(data?.error || 'Apple Sign In failed');
    }

    if (!data?.access_token || !data?.refresh_token) {
      throw new Error('No session tokens returned from server');
    }
    
    const { data: sessionResult, error: sessionError } = await supabase.auth.setSession({
      access_token: data.access_token,
      refresh_token: data.refresh_token,
    });
    
    if (sessionError) {
      throw sessionError;
    }
    
    if (!sessionResult?.session) {
      throw new Error('Failed to set session');
    }

    return {
      success: true,
      data: { session: data.session, user: data.user },
    };
  } catch (error: any) {
    // Check if user cancelled the sign-in dialog
    if (error?.isCancellation) {
      console.log('[Apple Sign In] User cancelled sign-in dialog');
      return {
        success: false,
        error: 'CANCELLED', // Special error code for cancellations
      };
    }
    
    // Extract the most detailed error message possible
    let errorMessage = 'Failed to sign in with Apple';
    
    if (error) {
      if (error.message) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (error.toString && error.toString() !== '[object Object]') {
        errorMessage = error.toString();
      }
    }
    
    console.error('[Apple Sign In] Error:', error);
    showToast?.(errorMessage, 'error');
    
    return {
      success: false,
      error: errorMessage,
    };
  }
};
